import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  FiCreditCard,
  FiPlus,
  FiSearch,
  FiArrowUpRight,
  FiArrowDownLeft,
  FiTrash2,
  FiEdit2,
  FiRefreshCw,
  FiDollarSign,
  FiCalendar,
  FiInfo,
} from "react-icons/fi";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import ToolsHeader from "./ToolsHeader";
import {
  listCashbooks,
  addCashbook,
  editCashbook,
  getCashbookById,
  deleteCashbook,
  getCashbookEntries,
  addCashbookEntry,
  editCashbookEntry,
  deleteCashbookEntry,
} from "../../api/tools/toolsApi";
import "./Tools.css";

const CashbookTool = () => {
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token =
    loggedInUser?.refreshToken ||
    authState?.refreshToken ||
    loggedInUser?.token ||
    authState?.token;

  // Cashbooks list state
  const [cashbooks, setCashbooks] = useState([]);
  const [selectedCashbook, setSelectedCashbook] = useState(null);
  const [loadingCashbooks, setLoadingCashbooks] = useState(true);
  const [cashbookSearch, setCashbookSearch] = useState("");

  // Cashbook entries state
  const [entries, setEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [filterType, setFilterType] = useState("ALL"); // ALL, CREDIT, DEBIT

  // Modals
  const [showCashbookModal, setShowCashbookModal] = useState(false);
  const [cashbookForm, setCashbookForm] = useState({ id: null, name: "", description: "" });
  const [submittingCashbook, setSubmittingCashbook] = useState(false);

  const [showEntryModal, setShowEntryModal] = useState(false);
  const [entryForm, setEntryForm] = useState({
    id: null,
    entry_type: "CREDIT",
    amount: "",
  });
  const [submittingEntry, setSubmittingEntry] = useState(false);

  // 1. Fetch All Cashbooks
  const fetchCashbooks = useCallback(async () => {
    if (!token) return;
    setLoadingCashbooks(true);
    try {
      const data = await listCashbooks(token, {
        page_number: 1,
        page_size: 1000,
        keyword_search: cashbookSearch,
      });
      const list =
        data?.cashbooks ||
        data?.data ||
        (Array.isArray(data) ? data : []);
      setCashbooks(list);

      if (list.length > 0 && !selectedCashbook) {
        setSelectedCashbook(list[0]);
      } else if (list.length === 0) {
        setSelectedCashbook(null);
      }
    } catch (err) {
      console.error("Error fetching cashbooks:", err);
      toast.error(err?.response?.data?.message || "Failed to load Cashbooks");
    } finally {
      setLoadingCashbooks(false);
    }
  }, [token, cashbookSearch, selectedCashbook]);

  useEffect(() => {
    fetchCashbooks();
  }, [fetchCashbooks]);

  // 2. Fetch Entries for selected Cashbook
  const fetchEntries = useCallback(async (cashbookId) => {
    if (!token || !cashbookId) return;
    setLoadingEntries(true);
    try {
      const [entriesRes, detailsRes] = await Promise.allSettled([
        getCashbookEntries(token, cashbookId, { page_number: 1, page_size: 1000 }),
        getCashbookById(token, cashbookId),
      ]);

      if (entriesRes.status === "fulfilled") {
        const list =
          entriesRes.value?.cashbookEntries ||
          entriesRes.value?.entries ||
          entriesRes.value?.data ||
          (Array.isArray(entriesRes.value) ? entriesRes.value : []);
        setEntries(list);
      } else {
        setEntries([]);
      }

      if (detailsRes.status === "fulfilled") {
        const details = detailsRes.value?.cashbook || detailsRes.value?.data || detailsRes.value;
        if (details && details.id) {
          setSelectedCashbook((prev) => ({ ...prev, ...details }));
        }
      }
    } catch (err) {
      console.error("Error loading cashbook entries:", err);
    } finally {
      setLoadingEntries(false);
    }
  }, [token]);

  useEffect(() => {
    if (selectedCashbook?.id) {
      fetchEntries(selectedCashbook.id);
    }
  }, [selectedCashbook?.id, fetchEntries]);

  // 3. Add or Edit Cashbook
  const handleSaveCashbook = async (e) => {
    e.preventDefault();
    if (!cashbookForm.name.trim()) {
      toast.warning("Please enter a cashbook name");
      return;
    }
    setSubmittingCashbook(true);
    try {
      if (cashbookForm.id) {
        await editCashbook(token, {
          id: cashbookForm.id,
          name: cashbookForm.name,
          description: cashbookForm.description,
        });
        toast.success("Cashbook updated successfully");
      } else {
        const res = await addCashbook(token, {
          name: cashbookForm.name,
          description: cashbookForm.description,
        });
        toast.success("Cashbook created successfully");
        const created = res?.cashbook || res?.data || res;
        if (created?.id) setSelectedCashbook(created);
      }
      setShowCashbookModal(false);
      setCashbookForm({ id: null, name: "", description: "" });
      fetchCashbooks();
    } catch (err) {
      console.error("Error saving cashbook:", err);
      toast.error(err?.response?.data?.message || "Failed to save cashbook");
    } finally {
      setSubmittingCashbook(false);
    }
  };

  // 4. Delete Cashbook
  const handleDeleteCashbook = async (cashbookId) => {
    if (!window.confirm("Are you sure you want to delete this cashbook and its entries?")) return;
    try {
      await deleteCashbook(token, cashbookId);
      toast.success("Cashbook deleted");
      if (selectedCashbook?.id === cashbookId) {
        setSelectedCashbook(null);
        setEntries([]);
      }
      fetchCashbooks();
    } catch (err) {
      console.error("Error deleting cashbook:", err);
      toast.error(err?.response?.data?.message || "Failed to delete cashbook");
    }
  };

  // 5. Add or Edit Entry
  const handleSaveEntry = async (e) => {
    e.preventDefault();
    if (!selectedCashbook?.id) return;
    if (!entryForm.amount || Number(entryForm.amount) <= 0) {
      toast.warning("Please enter a valid amount");
      return;
    }
    setSubmittingEntry(true);
    try {
      if (entryForm.id) {
        await editCashbookEntry(token, {
          id: entryForm.id,
          cashbook_id: selectedCashbook.id,
          entry_type: entryForm.entry_type,
          amount: Number(entryForm.amount),
        });
        toast.success("Cashbook entry updated");
      } else {
        await addCashbookEntry(token, {
          cashbook_id: selectedCashbook.id,
          entry_type: entryForm.entry_type,
          amount: Number(entryForm.amount),
        });
        toast.success("Entry added to Cashbook");
      }
      setShowEntryModal(false);
      setEntryForm({ id: null, entry_type: "CREDIT", amount: "" });
      fetchEntries(selectedCashbook.id);
    } catch (err) {
      console.error("Error saving cashbook entry:", err);
      toast.error(err?.response?.data?.message || "Failed to save entry");
    } finally {
      setSubmittingEntry(false);
    }
  };

  // 6. Delete Entry
  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;
    try {
      await deleteCashbookEntry(token, entryId);
      toast.success("Entry deleted");
      if (selectedCashbook?.id) fetchEntries(selectedCashbook.id);
    } catch (err) {
      console.error("Error deleting entry:", err);
      toast.error(err?.response?.data?.message || "Failed to delete entry");
    }
  };

  // Computations
  const totalCredit = useMemo(() => {
    return entries
      .filter(
        (e) =>
          String(e.entry_type || e.type).toUpperCase() === "CREDIT" ||
          String(e.entry_type || e.type).toUpperCase() === "IN"
      )
      .reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  }, [entries]);

  const totalDebit = useMemo(() => {
    return entries
      .filter(
        (e) =>
          String(e.entry_type || e.type).toUpperCase() === "DEBIT" ||
          String(e.entry_type || e.type).toUpperCase() === "OUT"
      )
      .reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  }, [entries]);

  const netCashInHand = totalCredit - totalDebit;

  const filteredEntries = useMemo(() => {
    if (filterType === "ALL") return entries;
    return entries.filter(
      (e) => String(e.entry_type || e.type).toUpperCase() === filterType
    );
  }, [entries, filterType]);

  return (
    <div className="tools-container">
      <ToolsHeader
        title="Cashbook Tool"
        subtitle="Track daily cash inflow (Cash In / Credit) and outflow (Cash Out / Debit) in real time."
        actions={
          <div className="d-flex align-items-center gap-2">
            <button
              className="tools-btn tools-btn--secondary"
              onClick={fetchCashbooks}
              disabled={loadingCashbooks}
            >
              <FiRefreshCw className={loadingCashbooks ? "spin" : ""} /> Refresh
            </button>
            <button
              className="tools-btn tools-btn--primary"
              onClick={() => {
                setCashbookForm({ id: null, name: "", description: "" });
                setShowCashbookModal(true);
              }}
            >
              <FiPlus /> New Cashbook
            </button>
          </div>
        }
      />

      {/* Cashbook Selector Bar */}
      <div className="tools-action-bar">
        <div className="d-flex align-items-center gap-3 flex-wrap flex-grow-1">
          <div className="d-flex align-items-center gap-2">
            <FiCreditCard className="text-primary" size={20} />
            <span className="fw-bold text-dark">Active Cashbook:</span>
          </div>
          {loadingCashbooks ? (
            <span className="text-muted small">Loading cashbooks...</span>
          ) : cashbooks.length === 0 ? (
            <span className="text-muted small">No Cashbooks created yet.</span>
          ) : (
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {cashbooks.map((cb) => (
                <button
                  key={cb.id}
                  onClick={() => setSelectedCashbook(cb)}
                  className={`tools-btn tools-btn--sm ${
                    selectedCashbook?.id === cb.id
                      ? "tools-btn--primary"
                      : "tools-btn--secondary"
                  }`}
                >
                  {cb.name || `Cashbook #${cb.id}`}
                </button>
              ))}
            </div>
          )}
        </div>

        {selectedCashbook && (
          <div className="d-flex align-items-center gap-2">
            <button
              className="tools-btn tools-btn--sm tools-btn--secondary"
              title="Edit Cashbook"
              onClick={() => {
                setCashbookForm({
                  id: selectedCashbook.id,
                  name: selectedCashbook.name,
                  description: selectedCashbook.description || "",
                });
                setShowCashbookModal(true);
              }}
            >
              <FiEdit2 size={13} /> Edit
            </button>
            <button
              className="tools-btn tools-btn--sm tools-btn--secondary text-danger"
              title="Delete Cashbook"
              onClick={() => handleDeleteCashbook(selectedCashbook.id)}
            >
              <FiTrash2 size={13} /> Delete
            </button>
            <button
              className="tools-btn tools-btn--sm tools-btn--success"
              onClick={() => {
                setEntryForm({ id: null, entry_type: "CREDIT", amount: "" });
                setShowEntryModal(true);
              }}
            >
              <FiPlus /> Add Cash Entry
            </button>
          </div>
        )}
      </div>

      {/* Selected Cashbook Details & Stats */}
      {selectedCashbook && (
        <>
          {selectedCashbook.description && (
            <div
              className="mb-3 p-3 bg-white border rounded-3 text-secondary small d-flex align-items-center gap-2 shadow-xs"
            >
              <FiInfo className="text-primary flex-shrink-0" />
              <span>{selectedCashbook.description}</span>
            </div>
          )}

          <div className="tools-stats-grid">
            <div className="tools-stat-card">
              <div className="tools-stat-card__icon tools-stat-card__icon--green">
                <FiArrowDownLeft />
              </div>
              <div>
                <div className="tools-stat-card__label">Total Cash In (Credit)</div>
                <div className="tools-stat-card__value text-success">
                  ₹{Number(totalCredit).toLocaleString("en-IN")}
                </div>
              </div>
            </div>

            <div className="tools-stat-card">
              <div className="tools-stat-card__icon tools-stat-card__icon--red">
                <FiArrowUpRight />
              </div>
              <div>
                <div className="tools-stat-card__label">Total Cash Out (Debit)</div>
                <div className="tools-stat-card__value text-danger">
                  ₹{Number(totalDebit).toLocaleString("en-IN")}
                </div>
              </div>
            </div>

            <div className="tools-stat-card">
              <div className="tools-stat-card__icon tools-stat-card__icon--blue">
                <FiDollarSign />
              </div>
              <div>
                <div className="tools-stat-card__label">Net Cash in Hand</div>
                <div
                  className={`tools-stat-card__value ${
                    netCashInHand >= 0 ? "text-success" : "text-danger"
                  }`}
                >
                  ₹{Number(netCashInHand).toLocaleString("en-IN")}
                </div>
              </div>
            </div>

            <div className="tools-stat-card">
              <div className="tools-stat-card__icon tools-stat-card__icon--purple">
                <FiCreditCard />
              </div>
              <div>
                <div className="tools-stat-card__label">Total Entries</div>
                <div className="tools-stat-card__value text-dark">{entries.length}</div>
              </div>
            </div>
          </div>

          {/* Entries Filter and Action Bar */}
          <div className="tools-action-bar">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="fw-semibold text-muted small me-1">Filter by Type:</span>
              <button
                className={`tools-btn tools-btn--sm ${
                  filterType === "ALL" ? "tools-btn--primary" : "tools-btn--secondary"
                }`}
                onClick={() => setFilterType("ALL")}
              >
                All ({entries.length})
              </button>
              <button
                className={`tools-btn tools-btn--sm ${
                  filterType === "CREDIT" ? "tools-btn--success" : "tools-btn--secondary"
                }`}
                onClick={() => setFilterType("CREDIT")}
              >
                Cash In / Credit
              </button>
              <button
                className={`tools-btn tools-btn--sm ${
                  filterType === "DEBIT" ? "tools-btn--danger" : "tools-btn--secondary"
                }`}
                onClick={() => setFilterType("DEBIT")}
              >
                Cash Out / Debit
              </button>
            </div>

            <div className="d-flex gap-2">
              <button
                className="tools-btn tools-btn--sm tools-btn--success"
                onClick={() => {
                  setEntryForm({ id: null, entry_type: "CREDIT", amount: "" });
                  setShowEntryModal(true);
                }}
              >
                <FiArrowDownLeft /> + Cash In (Credit)
              </button>
              <button
                className="tools-btn tools-btn--sm tools-btn--danger"
                onClick={() => {
                  setEntryForm({ id: null, entry_type: "DEBIT", amount: "" });
                  setShowEntryModal(true);
                }}
              >
                <FiArrowUpRight /> - Cash Out (Debit)
              </button>
            </div>
          </div>

          {/* Entries Table */}
          {loadingEntries ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Loading entries...</p>
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="tools-empty-state">
              <div className="tools-empty-state__icon">
                <FiCreditCard />
              </div>
              <h4>No Entries in this Cashbook</h4>
              <p>Record your first Cash In or Cash Out transaction.</p>
              <button
                className="tools-btn tools-btn--primary"
                onClick={() => {
                  setEntryForm({ id: null, entry_type: "CREDIT", amount: "" });
                  setShowEntryModal(true);
                }}
              >
                <FiPlus /> Add Entry
              </button>
            </div>
          ) : (
            <div className="tools-table-card">
              <div className="tools-table-responsive">
                <table className="tools-table">
                  <thead>
                    <tr>
                      <th>S.No.</th>
                      <th>Entry Type</th>
                      <th>Amount</th>
                      <th>Created On</th>
                      <th style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEntries.map((entry, idx) => {
                      const isCredit =
                        String(entry.entry_type || entry.type).toUpperCase() === "CREDIT" ||
                        String(entry.entry_type || entry.type).toUpperCase() === "IN";
                      return (
                        <tr key={entry.id || idx}>
                          <td className="text-muted">{idx + 1}</td>
                          <td>
                            <span
                              className={`tools-badge ${
                                isCredit ? "tools-badge--credit" : "tools-badge--debit"
                              }`}
                            >
                              {isCredit ? (
                                <>
                                  <FiArrowDownLeft /> Cash In (Credit)
                                </>
                              ) : (
                                <>
                                  <FiArrowUpRight /> Cash Out (Debit)
                                </>
                              )}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`fw-bold fs-6 ${
                                isCredit ? "text-success" : "text-danger"
                              }`}
                            >
                              ₹{Number(entry.amount || 0).toLocaleString("en-IN")}
                            </span>
                          </td>
                          <td>
                            <span className="text-secondary small d-flex align-items-center gap-1">
                              <FiCalendar size={13} />
                              {entry.created_on
                                ? new Date(entry.created_on).toLocaleDateString("en-IN", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : entry.date || "Today"}
                            </span>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <div className="d-flex align-items-center justify-content-end gap-1">
                              <button
                                className="tools-btn--icon"
                                title="Edit Entry"
                                onClick={() => {
                                  setEntryForm({
                                    id: entry.id,
                                    entry_type: isCredit ? "CREDIT" : "DEBIT",
                                    amount: entry.amount,
                                  });
                                  setShowEntryModal(true);
                                }}
                              >
                                <FiEdit2 size={14} />
                              </button>
                              <button
                                className="tools-btn--icon tools-btn--icon-danger"
                                title="Delete Entry"
                                onClick={() => handleDeleteEntry(entry.id)}
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* When no cashbook exists */}
      {!selectedCashbook && !loadingCashbooks && (
        <div className="tools-empty-state">
          <div className="tools-empty-state__icon">
            <FiCreditCard />
          </div>
          <h4>No Cashbook Found</h4>
          <p>Create a Cashbook to start tracking daily site, personal, or business expenses.</p>
          <button
            className="tools-btn tools-btn--primary"
            onClick={() => {
              setCashbookForm({ id: null, name: "", description: "" });
              setShowCashbookModal(true);
            }}
          >
            <FiPlus /> Create Cashbook
          </button>
        </div>
      )}

      {/* Modal: Create/Edit Cashbook */}
      <Modal show={showCashbookModal} onHide={() => setShowCashbookModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 fw-bold">
            {cashbookForm.id ? "Edit Cashbook" : "Add New Cashbook"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveCashbook}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Cashbook Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Site Cashbook, Petty Cash, Daily Office"
                value={cashbookForm.name}
                onChange={(e) =>
                  setCashbookForm({ ...cashbookForm, name: e.target.value })
                }
                required
                autoFocus
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="e.g. Daily site expenses, tea, wages, materials"
                value={cashbookForm.description}
                onChange={(e) =>
                  setCashbookForm({ ...cashbookForm, description: e.target.value })
                }
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowCashbookModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submittingCashbook}>
              {submittingCashbook ? "Saving..." : cashbookForm.id ? "Update" : "Create Cashbook"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal: Add/Edit Cashbook Entry */}
      <Modal show={showEntryModal} onHide={() => setShowEntryModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 fw-bold">
            {entryForm.id ? "Edit Entry" : "Add Entry to Cashbook"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveEntry}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Entry Type *</Form.Label>
              <div className="d-flex gap-2">
                <Button
                  variant={entryForm.entry_type === "CREDIT" ? "success" : "outline-success"}
                  className="flex-grow-1"
                  type="button"
                  onClick={() => setEntryForm({ ...entryForm, entry_type: "CREDIT" })}
                >
                  <FiArrowDownLeft /> Cash In (Credit)
                </Button>
                <Button
                  variant={entryForm.entry_type === "DEBIT" ? "danger" : "outline-danger"}
                  className="flex-grow-1"
                  type="button"
                  onClick={() => setEntryForm({ ...entryForm, entry_type: "DEBIT" })}
                >
                  <FiArrowUpRight /> Cash Out (Debit)
                </Button>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Amount (₹) *</Form.Label>
              <Form.Control
                type="number"
                placeholder="e.g. 2500"
                value={entryForm.amount}
                onChange={(e) =>
                  setEntryForm({ ...entryForm, amount: e.target.value })
                }
                required
                min="1"
                autoFocus
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowEntryModal(false)}>
              Cancel
            </Button>
            <Button
              variant={entryForm.entry_type === "CREDIT" ? "success" : "danger"}
              type="submit"
              disabled={submittingEntry}
            >
              {submittingEntry ? "Saving..." : entryForm.id ? "Update Entry" : "Save Entry"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default CashbookTool;
